import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Button, IconButton } from "@material-ui/core";
import { Add as AddIcon, Save as SaveIcon, PictureAsPdf as PdfIcon, Visibility as VisibilityIcon, ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { changeTitleName, changePdfFileName, changeIndication } from "../../../redux/actions/emailAction";
import { getSelectedSheetsData, genDiuoPDF, getDiuoFile, postFormData } from "../Utils";
import history from "../../Routes/history";
import Header from "../../Header/Header";
import DocMenu from "../../DocMenu";
import NumDossDIUO from "../../NumDossDIUO";
import TableComponent from "./TableComponent";
import GenPdf from "../../GenPdf";
import LoadingOverlay from "../../LoadingOverlay";
import { checkboxes, checkboxesContentsConst } from "./Const";
import "../../../Styles/DIUO/Page7.scss";
const BASE_SHEET_COUNT = 40;

const getSheetNumber = (sheetKey) => {
  const match = String(sheetKey).match(/^sheet(\d+)$/);

  return match ? parseInt(match[1], 10) : 0;
};


const getSheetKey = (sheetNumber) => {
  return `sheet${sheetNumber}`;
};


const getDefaultSheetContent = (index) => {
  const content = checkboxesContentsConst[index] || {};

  return {
    description: content.description || "", localization: content.localization || "", natureOfInterventions: content.natureOfInterventions || "",
    access: content.access || "", risks: content.risks || "", securityMeasures: content.securityMeasures || ""
  };
};


const createBaseSheetDefinitions = () => {

  return checkboxes
    .slice(0, BASE_SHEET_COUNT)
    .map((title, index) => ({
      key: getSheetKey(index + 1),
      title,
      dynamic: false
    }));
};


const isImageFile = (value) => {
  return (
    typeof File !== "undefined" &&
    value instanceof File
  );
};


const DiuoPage7 = () => {
  const dispatch = useDispatch();

  const dossierValue = useSelector(
    (state) => state.devis.dossierValue
  );

  const index = useSelector(
    (state) => state.diuo.index
  );

  const diuoValue = useSelector(
    (state) => state.diuo.diuoValue
  );

  const [isLoading, setIsLoading] = useState(false);
  // Progression de l'enregistrement affichée dans le LoadingOverlay : { text, progress } ou null
  const [saveProgress, setSaveProgress] = useState(null);

  const [disableButton, setDisableButton] = useState(false);
  const [selectedSheets, setSelectedSheets] = useState({});
  const [savedSheetImages, setSavedSheetImages] = useState({});
  const [deletedImages, setDeletedImages] = useState(new Set());
  const [sheetDefinitions, setSheetDefinitions] = useState(createBaseSheetDefinitions);
  const [newSheetTitle, setNewSheetTitle] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);

  const markSheetImagesForDeletion = useCallback(
    (sheetKey) => {
      setDeletedImages((prev) => {
        const next = new Set(prev);

        next.add(`${sheetKey}_img1.jpg`);
        next.add(`${sheetKey}_img2.jpg`);

        Object.entries(savedSheetImages).forEach(
          ([compositeKey, filename]) => {
            if (
              compositeKey.startsWith(`${sheetKey}_`) &&
              typeof filename === "string" &&
              filename.trim() !== ""
            ) {
              next.add(filename);
            }
          }
        );

        return next;
      });

      setSavedSheetImages((prev) => {
        const next = { ...prev };

        Object.keys(next).forEach((compositeKey) => {
          if (compositeKey.startsWith(`${sheetKey}_`)) {
            delete next[compositeKey];
          }
        });

        return next;
      });
    },
    [savedSheetImages]
  );

  const resetData = useCallback(() => {
    setSelectedSheets({});
    setSavedSheetImages({});
    setDeletedImages(new Set());
    setSheetDefinitions(createBaseSheetDefinitions());
    setNewSheetTitle("");
  }, []);

  const loadData = useCallback(async () => {
    if (!dossierValue || index == null || !diuoValue) {
      resetData();
      return;
    }
    setIsLoading(true);

    try {
      const { selectedSheets: loadedSelectedSheets, images } = await getSelectedSheetsData(dossierValue, index, diuoValue);
      const databaseSheets = loadedSelectedSheets || {};

      const baseDefinitions = createBaseSheetDefinitions();

      const dynamicDefinitions = Object.entries(databaseSheets).filter(([sheetKey]) => {
        return (
          getSheetNumber(sheetKey) >
          BASE_SHEET_COUNT
        );
      }).map(([sheetKey, sheetData]) => ({
        key: sheetKey,
        title: sheetData.title || sheetKey,
        dynamic: true
      }));
      const definitionsMap = new Map();

      baseDefinitions.forEach((definition) => {
        definitionsMap.set(definition.key, definition);
      });

      dynamicDefinitions.forEach((definition) => {
        definitionsMap.set(definition.key, definition);
      });

      const finalDefinitions = Array.from(definitionsMap.values()).sort((a, b) => {
        return (
          getSheetNumber(a.key) -
          getSheetNumber(b.key)
        );
      });

      setSheetDefinitions(finalDefinitions);

      const updatedSheets = { ...databaseSheets };
      const newSavedImages = {};
      const loadedImages = Array.isArray(images) ? images : [];

      for (const image of loadedImages) {
        if (!image || !image.name || !image.img) {
          continue;
        }

        const match = image.name.match(/^(sheet\d+)_img(\d+)\.[^.]+$/i);

        if (!match) {
          continue;
        }

        const sheetKey = match[1];
        const imgNumber = match[2];

        if (!updatedSheets[sheetKey]) {
          continue;
        }

        let buffer = null;

        if (image.img.buffer && Array.isArray(image.img.buffer.data)) {
          buffer = new Uint8Array(image.img.buffer.data).buffer;
        } else if (
          image.img.buffer instanceof Uint8Array
        ) {
          buffer = image.img.buffer.buffer;
        } else if (
          image.img.buffer instanceof ArrayBuffer
        ) {
          buffer = image.img.buffer;
        } else {
          continue;
        }

        const imageFile = new File([buffer], image.name, {
          type:
            image.img.type ||
            "image/jpeg"
        });

        const fieldName = `img${imgNumber}`;

        updatedSheets[sheetKey] = { ...updatedSheets[sheetKey], [fieldName]: imageFile };

        const compositeKey = `${sheetKey}_${fieldName}`;

        newSavedImages[compositeKey] = image.name;
      }

      Object.keys(updatedSheets).forEach(
        (sheetKey) => {
          if (updatedSheets[sheetKey].img1 === undefined) {
            updatedSheets[sheetKey].img1 = null;
          }

          if (updatedSheets[sheetKey].img2 === undefined) {
            updatedSheets[sheetKey].img2 = null;
          }
        }
      );

      setSelectedSheets(updatedSheets);
      setSavedSheetImages(newSavedImages);
      setDeletedImages(new Set());

    } catch (err) {
      console.error("Erreur lors du chargement des données:", err);
    } finally {
      setIsLoading(false);
    }
  }, [dossierValue, index, diuoValue, resetData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddNewSheet = () => {
    const title = newSheetTitle.trim();

    if (!title) {
      return;
    }

    const existingNumbers = sheetDefinitions.map((definition) => getSheetNumber(definition.key)).filter((number) => number > 0);

    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : BASE_SHEET_COUNT;

    const newSheetNumber = Math.max(BASE_SHEET_COUNT + 1, maxNumber + 1);

    const newSheetKey = getSheetKey(newSheetNumber);

    setSheetDefinitions((prev) => {
      const next = [
        ...prev,
        {
          key: newSheetKey,
          title,
          dynamic: true
        }
      ];

      return next.sort((a, b) => {
        return (
          getSheetNumber(a.key) -
          getSheetNumber(b.key)
        );
      });
    });

    setSelectedSheets((prev) => ({
      ...prev, [newSheetKey]: {
        title,
        description: "",
        localization: "",
        natureOfInterventions: "",
        access: "",
        risks: "",
        securityMeasures: "",
        img1: null,
        img2: null
      }
    }));

    setNewSheetTitle("");
  };

  const handleDeleteSheet = (sheetKey) => {
    const definition =
      sheetDefinitions.find(
        (item) => item.key === sheetKey
      );

    if (!definition || !definition.dynamic) {
      return;
    }

    markSheetImagesForDeletion(sheetKey);

    setSelectedSheets((prev) => {
      const next = { ...prev };

      delete next[sheetKey];

      return next;
    });

    setSheetDefinitions((prev) => {
      return prev.filter(
        (definition) =>
          definition.key !== sheetKey
      );
    });
  };

  const handleCheckboxChange = (sheetDefinition) => {
    const { key, title } = sheetDefinition;

    if (selectedSheets[key]) {
      markSheetImagesForDeletion(key);
      setSelectedSheets((prev) => {
        const next = { ...prev };

        delete next[key];
        return next;
      });
      return;
    }

    const sheetNumber = getSheetNumber(key);

    const defaultContent = sheetNumber > 0 && sheetNumber <= BASE_SHEET_COUNT ? getDefaultSheetContent(sheetNumber - 1) : {
      description: "",
      localization: "",
      natureOfInterventions: "",
      access: "",
      risks: "",
      securityMeasures: ""
    };

    setSelectedSheets((prev) => ({
      ...prev, [key]: {
        title,

        description:
          defaultContent.description,

        localization:
          defaultContent.localization,

        natureOfInterventions:
          defaultContent.natureOfInterventions,

        access:
          defaultContent.access,

        risks:
          defaultContent.risks,

        securityMeasures:
          defaultContent.securityMeasures,

        img1: null,
        img2: null
      }
    }));
  };


  const handleInputChange = (sheetKey, field, value) => {
    if (field.startsWith("img")) {
      const compositeKey =
        `${sheetKey}_${field}`;
      const oldSavedFilename = savedSheetImages[compositeKey];

      if (oldSavedFilename && (value === null || isImageFile(value))) {
        setDeletedImages((prev) => {
          const next = new Set(prev);

          next.add(oldSavedFilename);

          return next;
        });

        setSavedSheetImages((prev) => {
          const next = { ...prev };

          delete next[compositeKey];

          return next;
        });
      }
    }

    setSelectedSheets((prev) => {
      const currentSheet = prev[sheetKey];

      if (!currentSheet) {
        return prev;
      }

      return {
        ...prev,

        [sheetKey]: {
          ...currentSheet,
          [field]: value
        }
      };
    });
  };

  const sortedKeys = Object.keys(
    selectedSheets
  ).sort((a, b) => {
    return (
      getSheetNumber(a) -
      getSheetNumber(b)
    );
  });

  const handleSaveData = async (e) => {
    e.preventDefault();

    if (disableButton) {
      return;
    }

    if (!dossierValue) {
      console.error(
        "Impossible d'enregistrer : dossierValue absent."
      );

      return;
    }

    if (index == null) {
      console.error(
        "Impossible d'enregistrer : index absent."
      );

      return;
    }

    if (!diuoValue) {
      return;
    }

    setDisableButton(true);
    setIsLoading(true);

    try {
      const formData = new FormData();

      formData.append(
        "ndossier",
        dossierValue
      );

      formData.append(
        "index",
        index
      );

      formData.append(
        "diuoValue",
        diuoValue
      );

      const imagesToUpload = [];

      /**
       * Envoi des fiches sélectionnées.
       */
      Object.keys(selectedSheets).forEach(
        (sheetKey) => {
          const sheet =
            selectedSheets[sheetKey];

          if (!sheet) {
            return;
          }

          formData.append(
            `${sheetKey}_title`,
            sheet.title || ""
          );

          formData.append(
            `${sheetKey}_description`,
            sheet.description || ""
          );

          formData.append(
            `${sheetKey}_localization`,
            sheet.localization || ""
          );

          formData.append(
            `${sheetKey}_natureOfInterventions`,
            sheet.natureOfInterventions || ""
          );

          formData.append(
            `${sheetKey}_access`,
            sheet.access || ""
          );

          formData.append(
            `${sheetKey}_risks`,
            sheet.risks || ""
          );

          formData.append(
            `${sheetKey}_securityMeasures`,
            sheet.securityMeasures || ""
          );

          ["img1", "img2"].forEach((field) => {
            const value = sheet[field];
            const compositeKey = `${sheetKey}_${field}`;
            const isPreviouslySaved = !!savedSheetImages[compositeKey];

            if (isImageFile(value) && !isPreviouslySaved) {
              imagesToUpload.push({
                fieldname: compositeKey,
                file: value
              });
            }
          }
          );
        }
      );

      const imagesToDelete = Array.from(deletedImages);

      if (imagesToDelete.length > 0) {
        formData.append(
          "imagesToDelete",
          JSON.stringify(
            imagesToDelete
          )
        );
      }

      // Étapes : les fiches, chaque image, puis le rechargement
      const totalSteps = imagesToUpload.length + 2;

      // Textes et suppressions d'abord : une image remplacée doit être supprimée avant l'envoi de la nouvelle
      setSaveProgress({ text: "Enregistrement des fiches...", progress: 0 });
      await postFormData("/api/diuo/saveSelectedSheets", formData);

      // Puis les images une par une : une seule requête avec toutes les images ne répondait jamais sur Gandi
      for (const [i, { fieldname, file }] of imagesToUpload.entries()) {
        setSaveProgress({
          text: "Enregistrement...",
          progress: (i + 1) / totalSteps
        });

        const imageFormData = new FormData();
        imageFormData.append("ndossier", dossierValue);
        imageFormData.append("index", index);
        imageFormData.append("fieldname", fieldname);
        imageFormData.append("image", file);

        await postFormData("/api/diuo/saveSelectedSheetImage", imageFormData);
      }

      setSaveProgress({ text: "Rechargement des données...", progress: (totalSteps - 1) / totalSteps });
      await loadData();

      setDeletedImages(new Set());

    } catch (err) {
      console.error("Erreur lors de l'enregistrement des fiches techniques:", err);
      alert(err.name === "AbortError"
        ? "L'enregistrement a pris trop de temps. Rechargez la page pour vérifier ce qui a été enregistré."
        : `Erreur lors de l'enregistrement des fiches techniques : ${err.message}`);
    } finally {
      setIsLoading(false);
      setSaveProgress(null);
      setDisableButton(false);
    }
  };

  const handleGenClick = useCallback(async () => {
    if (disableButton) {
      return;
    }
    setDisableButton(true);
    setFile(null);

    try {
      const blob = await genDiuoPDF(diuoValue, dossierValue, index);

      if (blob) {
        setIsOpen(true);
        setFile(blob);

        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    } catch (err) {
      console.error("Erreur lors de la génération du PDF:", err);
    } finally {
      setDisableButton(false);
    }
  }, [disableButton, diuoValue, dossierValue, index]);


  const handleShowPdf = useCallback(async () => {
    if (disableButton) {
      return;
    }

    setDisableButton(true);
    setFile(null);

    try {
      const response = await getDiuoFile(dossierValue, index, "pdf");

      if (response && response.blob && response.blob.size > 100) {
        setFile(response.blob);
        setIsOpen(true);
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    } catch (err) {
      console.error("Erreur lors de l'affichage du PDF:", err);
    } finally {
      setDisableButton(false);
    }
  }, [disableButton, dossierValue, index]);

  const handleGetPdfFile =
    useCallback(async () => {
      if (disableButton) {
        return;
      }

      setDisableButton(true);
      setFile(null);

      try {
        const response = await getDiuoFile(dossierValue, index, "pdf");

        if (response && response.blob && response.blob.type === "application/pdf") {
          setFile(response.blob);
          dispatch(changePdfFileName(response.fileName));
        }
      } catch (err) {
        console.error("Erreur lors de la récupération du PDF:", err);
      } finally {
        setDisableButton(false);
      }
    }, [disableButton, dossierValue, index, dispatch]);

  const handleGetWordFile =
    useCallback(async () => {
      if (disableButton) {
        return;
      }

      setDisableButton(true);
      setFile(null);

      try {
        const response = await getDiuoFile(dossierValue, index, 'docx');
        if (response && response.blob && response.blob.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
          setFile(response.blob);
          dispatch(changePdfFileName(response.fileName));
        }
      } catch (err) {
        console.error(
          "Erreur lors de la récupération du Word:",
          err
        );
      } finally {
        setDisableButton(false);
      }
    }, [disableButton, dossierValue, index, dispatch]);

  const handleChangeTitleName =
    useCallback(() => {
      dispatch(
        changeTitleName("DIUO")
      );
    }, [dispatch]);

  const prevPage = () => {
    history.push("/diuo/page6");
  };

  const nextPage = () => {
    history.push("/diuo/page8");
  };

  return (
    <div className="diuo-page7">
      {isLoading && (
        <LoadingOverlay text={saveProgress?.text} progress={saveProgress?.progress} />
      )}
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={
          handleChangeTitleName
        }
        updateIndication={() =>
          dispatch(
            changeIndication(index)
          )
        }
      />
      <h1 className="title"> DIUO (Fiches techniques) </h1>
      <NumDossDIUO />
      <div className="container">
        <div>
          <div className="construction-elements__add-new">
            <input
              type="text"
              placeholder="Nouvelle fiche technique"
              value={newSheetTitle}
              onChange={(e) => setNewSheetTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddNewSheet();
                }
              }}
              style={{
                marginTop: "25px",
                boxShadow:
                  "0 0 7px #7a7a7a"
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewSheet}
              disabled={disableButton}
            >
              Ajouter
            </Button>
          </div>
          <div className="checkbox-grid">
            {sheetDefinitions.map(
              (definition) => {
                const {
                  key,
                  title,
                  dynamic
                } = definition;
                const sheetIndex = getSheetNumber(key) - 1;
                const isSelected = selectedSheets[key] !== undefined;
                return (
                  <div
                    key={key}
                    className={sheetIndex % 2 === 0 ? "checkbox-item-left" : "checkbox-item-right"}
                    style={{
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <label>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(definition)}
                        disabled={disableButton}
                      />
                      &nbsp; {title}
                    </label>
                    {dynamic && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSheet(key)}
                        disabled={disableButton}
                        style={{
                          padding: "2px",
                          color: "#f44336",
                          marginLeft: "8px",
                          minWidth: "auto"
                        }}
                        title="Supprimer cette fiche technique"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                );
              }
            )}
          </div>
        </div>
        <div className="table-container">
          {sortedKeys.map((key, tableIndex) => {
            const sheetNumber = getSheetNumber(key);
            return (
              <TableComponent
                index={tableIndex}
                key={key}
                sheet={selectedSheets[key]}
                sheetNumber={sheetNumber}
                onInputChange={(field, value) =>
                  handleInputChange(key, field, value)
                }
              />
            );
          })}
        </div>
      </div>
      <div className="buttons-container">
        <Button
          color="primary"
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveData}
          disabled={disableButton || !diuoValue}
        >
          Enregistrer
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<PdfIcon />}
          onClick={handleGenClick}
          disabled={disableButton || !diuoValue}
        >
          Générer
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<VisibilityIcon />}
          onClick={handleShowPdf}
          disabled={disableButton || !diuoValue}
        >
          Afficher
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={prevPage}
          disabled={disableButton || !diuoValue}
        >
          Précédent
        </Button>
        <Button
          color="primary"
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={nextPage}
          disabled={disableButton || !diuoValue}
        >
          Suivant
        </Button>
        {disableButton && (
          <CircularProgress className="spinner" color="inherit" />
        )}
      </div>
      <GenPdf
        file={file}
        isOpen={isOpen}
        closeDoc={() => setIsOpen(false)}
        scale={1}
      />
    </div>
  );
};
export default DiuoPage7;